import {
    Button,
    Text,
    FormControl,
    FormLabel,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Textarea,
    VStack,
    Card,
    HStack,
    Select,
} from '@chakra-ui/react'
import { AccountRequestRole, Brand, InputCreateAccountRequest } from 'src/types/schema/graphql'
import { useLocationSlugStore } from '../state/useLocationSlugStore'
import AddressRow from 'src/components/layout/AddressRow'
import { CardImage } from 'src/features/account/components/favorites'
import { userStore } from 'src/features/auth/state/userStore'
import React from 'react'
import { useAlerts } from 'src/features/alerts/state/useAlerts'

interface IIsThisYourBreweryModal {
    brand?: Brand
}

export const IsThisYourBreweryModal: React.FC<IIsThisYourBreweryModal> = ({ brand }) => {
    const isThisYourBreweryModalIsOpen = useLocationSlugStore(
        (state) => state.isThisYourBreweryModalIsOpen
    )
    const setIsThisYourBreweryModalIsOpen = useLocationSlugStore(
        (state) => state.setIsThisYourBreweryModalIsOpen
    )
    const { user, createAccountRequest } = userStore()
    const [isLoading, setIsLoading] = React.useState(false)
    const [requestFormData, setRequestFormData] = React.useState<InputCreateAccountRequest>({
        claimedBrand: brand.id,
        givenName: user ? user.givenName : '',
        familyName: user ? user.familyName : '',
        contact: {
            email: user ? user.contact.email : '',
        },
        requestedRole: null,
        notes: '',
    })

    React.useEffect(() => {
        setRequestFormData({
            ...requestFormData,
            givenName: user ? user.givenName : '',
            familyName: user ? user.familyName : '',
            contact: {
                email: user ? user.contact.email : '',
            },
        })
    }, [user])

    const sendClaimButtonDisabled =
        !requestFormData.givenName ||
        !requestFormData.familyName ||
        !requestFormData.contact.email ||
        !requestFormData.requestedRole

    const handleInputChange = (value: string, field: string) => {
        if (field === 'email') {
            setRequestFormData({
                ...requestFormData,
                contact: {
                    ...requestFormData.contact,
                    [field]: value,
                },
            })
        } else {
            setRequestFormData({
                ...requestFormData,
                [field]: value,
            })
        }
    }

    const handleClaimBrewery = async () => {
        setIsLoading(true)
        try {
            const response = await createAccountRequest(requestFormData)
            if (response) {
                useAlerts.getState().addAlert({
                    id: 'successfully-created-account-request',
                    title: 'Success!',
                    description:
                        'Your claim request has been sent to BrewLogix staff for review. You will receive an email with further instructions.',
                    theme: 'success',
                    status: 'active',
                    duration: 6,
                })
                setIsThisYourBreweryModalIsOpen(false)
            }
        } catch (err) {
            useAlerts.getState().addAlert({
                id: 'create-account-request-failed',
                title: 'Error',
                description: 'Unable to send claim request. Please try again.',
                theme: 'error',
                status: 'active',
                duration: 6,
            })
            console.error(err)
        }
        setIsLoading(false)
    }

    return (
        <Modal
            isOpen={isThisYourBreweryModalIsOpen}
            onClose={() => setIsThisYourBreweryModalIsOpen(false)}
            isCentered={true}
            size='lg'
        >
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <Text fontSize='2xl'>Claim this brewery</Text>
                </ModalHeader>
                <ModalCloseButton mt={1} size='lg' />
                <ModalBody>
                    <VStack spacing={6}>
                        <Text>
                            Please fill out the following form to claim your brewery. BrewLogix
                            staff will validate your claim on this brand and reach out with details
                            via email.
                        </Text>
                        <Card className='w-full border border-solid border-gray-100 p-4'>
                            <HStack spacing={3}>
                                <CardImage
                                    asset={brand.assets?.[0]}
                                    alt={brand.assets?.[0]?.alt}
                                    customClasses='h-12 w-12'
                                />
                                <VStack spacing={1} align='start'>
                                    <Text fontWeight={700}>
                                        {brand?.title || brand?.displayTitle}
                                    </Text>
                                    <AddressRow address={brand?.address} />
                                </VStack>
                            </HStack>
                        </Card>
                        <FormControl isRequired>
                            <FormLabel fontSize='sm' fontWeight={700}>
                                First Name
                            </FormLabel>
                            <Input
                                required
                                placeholder='Enter full name'
                                value={requestFormData.givenName}
                                onChange={(e) => handleInputChange(e.target.value, 'givenName')}
                            />
                        </FormControl>
                        <FormControl isRequired>
                            <FormLabel fontSize='sm' fontWeight={700}>
                                Last Name
                            </FormLabel>
                            <Input
                                required
                                placeholder='Enter full name'
                                value={requestFormData.familyName}
                                onChange={(e) => handleInputChange(e.target.value, 'familyName')}
                            />
                        </FormControl>
                        <FormControl isRequired>
                            <FormLabel fontSize='sm' fontWeight={700}>
                                Email
                            </FormLabel>
                            <Input
                                required
                                placeholder='Enter email address'
                                value={requestFormData.contact.email}
                                onChange={(e) => handleInputChange(e.target.value, 'email')}
                            />
                        </FormControl>
                        <FormControl isRequired>
                            <FormLabel fontSize='sm' fontWeight={700}>
                                Your Role
                            </FormLabel>
                            <Select
                                className='w-full'
                                placeholder='Select'
                                onChange={(e) => handleInputChange(e.target.value, 'requestedRole')}
                            >
                                {Object.values(AccountRequestRole).map((value) => (
                                    <option key={value} value={value}>
                                        {value.replace(/___/g, ' ')}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl>
                            <FormLabel fontSize='sm' fontWeight={700}>
                                Message
                            </FormLabel>
                            <Textarea
                                placeholder='Leave a message for the Customer Success team.'
                                onChange={(e) => handleInputChange(e.target.value, 'notes')}
                            />
                        </FormControl>
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button
                        variant='default'
                        size='sm'
                        mr={3}
                        onClick={() => setIsThisYourBreweryModalIsOpen(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant='secondary'
                        size='sm'
                        isDisabled={sendClaimButtonDisabled}
                        isLoading={isLoading}
                        onClick={handleClaimBrewery}
                    >
                        Send Claim
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    )
}
